/**
 * Sizer settings processing and AHU sizing logic
 */
import { IBotData, IAHUParams, ITunnel } from '../../models';
import {
  SizerSettings,
  SizerResult,
  SizerMatrixData,
  DEFAULT_COEFFICIENTS,
} from '../../models/bot';
import { interpolateExtrapolate, transformValue } from './designAHU.utils';
import { dispatchMutation, dispatchQuery } from '../../utils/dispatch.utils';
import { kaiSizerApi } from '../../services';
/**
 * Gets sizer settings from localStorage
 */
function getSizerSettingsFromStorage(): any {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const stored = localStorage.getItem('sizer_settings');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Ignore parse errors
  }
  return {};
}
/**
 * Processes sizer settings from storage
 */
function processSizerSettings(settingsFromStorage: any): any {
  const tubes: { [key: number]: number } = {
    2: 0.375,
    3: 0.5,
    4: 0.625,
  };
  const casingFlange: { [key: string]: number } = {
    '0.5': 1,
    '0.75': 2,
    '1.00': 3,
    '1.25': 4,
    '1.50': 5,
    '2.00': 6,
  };
  const processedSettings: any = { ...settingsFromStorage };

  Object.keys(processedSettings).forEach((key) => {
    if (
      (key === 'cc_top_and_btm_casing_flange' ||
        key === 'hc_top_and_btm_casing_flange') &&
      !processedSettings[`${key}_id`] &&
      casingFlange[processedSettings[key]]
    ) {
      processedSettings[`${key}_id`] = casingFlange[processedSettings[key]];
    }
    if (
      key.includes('_id') &&
      key !== 'casing_insulation_id' &&
      key !== 'cc_top_and_btm_casing_flange_id' &&
      key !== 'hc_top_and_btm_casing_flange_id'
    ) {
      delete processedSettings[key];
    } else if (key === 'allowable_filter_sizes') {
      if (Array.isArray(processedSettings[key])) {
        processedSettings[key] = processedSettings[key]
          .map((el: any) => el.value)
          .join(',');
      }
    } else if (
      (key === 'cc_tube' || key === 'hc_tube') &&
      tubes[processedSettings[key]]
    ) {
      processedSettings[key] =
        processedSettings[`${key}_value`] || tubes[processedSettings[key]];
    } else if (!processedSettings[key]) {
      const val = processedSettings[key.replace('_id', '')];
      if (val) {
        processedSettings[key] = +val;
      } else {
        delete processedSettings[key];
      }
    } else {
      processedSettings[key] = +processedSettings[key];
    }
  });
  if (
    processedSettings.casing_insulation &&
    !processedSettings.casing_insulation_id
  ) {
    processedSettings.casing_insulation_id =
      processedSettings.casing_insulation;
  }
  delete processedSettings.hc_tube_value;
  delete processedSettings.cc_tube_value;
  delete processedSettings.casing_insulation;
  return processedSettings;
}
/**
 * Creates default sizer settings
 */
function createDefaultSizerSettings(
  botData: IBotData,
  processedSettings: any,
  optimal: [number, number, number],
  casingInsulationId: number | null,
  volume: number,
): SizerSettings {
  return {
    product_name: 1,
    plenum_fan_max_wide: 8,
    plenum_fan_max_high: 8,
    cube_fan_max_wide: 8,
    cube_fan_max_high: 8,
    plug_fan_max_wide: 8,
    plug_fan_max_high: 8,
    cc_face_vel_min: 200,
    cc_face_vel_max: 500,
    cc_tube: 0.625,
    cc_top_and_btm_casing_flange: 1.5,
    cc_top_and_btm_casing_flange_id: 5,
    cc_max_fh: 48,
    cc_qty_stacked: 3,
    hc_face_vel_min: 200,
    hc_face_vel_max: 800,
    hc_tube: 0.625,
    hc_top_and_btm_casing_flange: 1.5,
    hc_top_and_btm_casing_flange_id: 5,
    hc_max_fh: 60,
    hc_qty_stacked: 3,
    filter_face_vel_min: 200,
    filter_face_vel_max: 600,
    filter_frame_loading_id: 1,
    casing_insulation_id: casingInsulationId,
    allowable_filter_sizes: 'w12_h24,w24_h12,w20_h20,w20_h24,w24_h20,w24_h24',
    max_different_sizes: 3,
    ...processedSettings,
    airflow: volume,
    optimal,
    page_coordinates: [0, 0],
    construction_class_id: botData.construction_class_id,
  };
}
//
function getCoordinats(height: number, width: number, ahuParams: IAHUParams) {
  const dimensions = ahuParams.dimensions;
  const diff_h =
    (height - dimensions.tunnel_cabinet_height_min) /
    dimensions.tunnel_cabinet_height_increment /
    10;
  const diff_w =
    (width - dimensions.tunnel_cabinet_width_min) /
    dimensions.tunnel_cabinet_width_increment /
    10;
  let x = Math.floor(diff_w);
  let y = Math.floor(diff_h);
  const w_min =
    dimensions.tunnel_cabinet_width_min +
    x * dimensions.tunnel_cabinet_width_increment * 10;
  const w_max = w_min + 9 * dimensions.tunnel_cabinet_width_increment;
  if (width < w_min) x -= 1;
  else if (width > w_max) x += 1;
  const h_min =
    dimensions.tunnel_cabinet_height_min +
    y * dimensions.tunnel_cabinet_height_increment * 10;
  const h_max = h_min + 9 * dimensions.tunnel_cabinet_height_increment;
  if (height < h_min) y -= 1;
  else if (height > h_max) y += 1;
  return [x, y];
}

/**
 * Gets the appropriate volume based on air_stream_id
 */
export function getVolumeForAirStream(
  botData: IBotData,
  airStreamId: number,
): number {
  // air_stream_id: 1 = Supply Air (SA)
  // air_stream_id: 2 = Return Air (RA)
  // air_stream_id: 4 = Exhaust Air (EA)
  if (airStreamId === 2) {
    return +(botData.ra_volume_max ?? botData.sa_volume_max ?? 0);
  } else if (airStreamId === 4) {
    return +(botData.ea_volume_max ?? botData.sa_volume_max ?? 0);
  } else {
    // Default to supply air (air_stream_id: 1 or any other value)
    return +botData.sa_volume_max;
  }
}

/**
 * Processes sizer settings and sizes the AHU
 */
export async function processSizerAndSizeAHU(
  botData: IBotData,
  ahuParams: IAHUParams,
  tunnel: ITunnel,
  index: number,
  defaultThickness: number | null,
  defaultCasingInsulationId: number | null,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
): Promise<SizerResult> {
  // Get optimal sizes
  let optimals: Array<[number, number, number]> = [];
  try {
    optimals = (await dispatchQuery(
      kaiSizerApi.endpoints.getOptimalSizes,
      undefined,
      getOptions(),
    )) as Array<[number, number, number]>;
  } catch (e) {
    // Use default if API fails
  }
  // Get and process sizer settings from storage
  const sizerSettingsFromStorage = getSizerSettingsFromStorage();
  const processedSettings = processSizerSettings(sizerSettingsFromStorage);

  // Get tunnel index to determine air_stream_id
  const tunnelAirStreamId = botData.tunnels[index].air_stream_id;
  const volume = getVolumeForAirStream(botData, tunnelAirStreamId);
  const optimal = interpolateExtrapolate(optimals, volume);
  // Create sizer settings
  let sizerSettings = createDefaultSizerSettings(
    botData,
    processedSettings,
    optimal,
    defaultCasingInsulationId,
    volume,
  );
  // Get thickness from casing insulations
  const insulation =
    ahuParams.casing?.insulations?.find(
      (el) => el.id === sizerSettings.casing_insulation_id,
    ) || ahuParams.casing?.insulations[0];
  sizerSettings.casing_insulation_id = insulation?.id;
  let thickness = insulation?.thickness || defaultThickness;
  sizerSettings.thickness = thickness;
  const casingInsulationId = sizerSettings.casing_insulation_id;
  // Adjust optimal based on tunnel dimensions
  if (+tunnel.width > +sizerSettings.optimal[1]) {
    sizerSettings.optimal[1] = +tunnel.width;
  }
  if (+tunnel.height > +sizerSettings.optimal[2]) {
    sizerSettings.optimal[2] = +tunnel.height;
  }
  // Size AHU
  const sizerResp = (await dispatchMutation(
    kaiSizerApi.endpoints.getSizerMatrixData,
    { body: sizerSettings },
    getOptions(),
  )) as SizerMatrixData;
  const dimensions = ahuParams.dimensions;
  const height = Math.max(
    tunnel.height,
    transformValue(
      Number(sizerResp.height),
      dimensions?.tunnel_cabinet_height_increment || 1,
    ),
  );
  const width = Math.max(
    tunnel.width,
    transformValue(
      Number(sizerResp.width),
      dimensions?.tunnel_cabinet_width_increment || 1,
    ),
  );
  // Get coordinates (simplified)
  const pageCoordinates = getCoordinats(height, width, ahuParams);
  sizerSettings.page_coordinates = pageCoordinates;
  const sizerRespLast = (await dispatchMutation(
    kaiSizerApi.endpoints.getSizerMatrixData,
    { body: sizerSettings },
    getOptions(),
  )) as SizerMatrixData;

  const filterByHeight =
    sizerRespLast.results?.find(
      (el: any) =>
        +el.current_height >= height &&
        height >=
          +el.current_height -
            (dimensions?.tunnel_cabinet_height_increment || 1),
    ) || sizerRespLast.results?.[0];
  const filterByWidth = filterByHeight?.elements?.find(
    (el) =>
      +el.current_width >= width &&
      width >= +el.current_width - dimensions.tunnel_cabinet_width_increment,
  );
  let ahuParamsResult: any = {};
  if (filterByHeight && filterByWidth) {
    ahuParamsResult = {
      ...filterByWidth,
      current_height: filterByHeight.current_height,
    };
  }
  onProgress?.(DEFAULT_COEFFICIENTS.size); // size coefficient
  return {
    sizerSettings,
    ahuParamsResult,
    thickness,
    casingInsulationId,
    tunnel,
  };
}
